import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import PageHeader from "@/components/common/PageHeader";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Briefcase, MapPin } from "lucide-react";
import api from "@/lib/api";

const blank = {
  companyName: "",
  designation: "",
  startDate: "",
  endDate: "",
  currentlyWorking: false,
  location: "",
  description: "",
  displayOrder: 1
};

export default function Experience() {
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchExperiences = async () => {
    try {
      setLoading(true);
      const res = await api.get("/Experience");
      if (res.success) {
        setItems(res.data || []);
      } else {
        toast.error(res.message || "Failed to fetch experiences");
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExperiences();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(blank);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      ...item,
      startDate: item.startDate ? item.startDate.substring(0, 7) : "",
      endDate: item.endDate ? item.endDate.substring(0, 7) : ""
    });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.companyName || !form.designation) {
      toast.error("Company and Designation are required.");
      return;
    }

    const payload = {
      ...form,
      startDate: form.startDate ? `${form.startDate}-01` : null,
      endDate: form.endDate ? `${form.endDate}-01` : null
    };

    try {
      if (editing) {
        const res = await api.put(`/Experience/${editing.id}`, payload);
        if (res.success) {
          toast.success("Experience updated.");
          fetchExperiences();
          setModalOpen(false);
        } else {
          toast.error(res.message || "Update failed");
        }
      } else {
        const res = await api.post("/Experience", payload);
        if (res.success) {
          toast.success("Experience added.");
          fetchExperiences();
          setModalOpen(false);
        } else {
          toast.error(res.message || "Creation failed");
        }
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    }
  };

  const handleDelete = async () => {
    try {
      const res = await api.delete(`/Experience/${toDelete.id}`);
      if (res.success) {
        toast.success("Experience removed.");
        fetchExperiences();
      } else {
        toast.error(res.message || "Failed to delete");
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    } finally {
      setConfirmOpen(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "";
    const [y, m] = d.split("-");
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    if (!m || !y) return d;
    return `${months[parseInt(m, 10) - 1]} ${y}`;
  };

  return (
    <div>
      <PageHeader
        title="Experience"
        description="Your professional journey, displayed as a timeline on your portfolio."
        testId="experience"
        action={openCreate}
        actionLabel="Add Experience"
        actionIcon={Plus}
      />

      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-4 sm:left-6 top-2 bottom-2 w-px bg-border" aria-hidden />

        <div className="space-y-5">
          {items.map((exp, idx) => (
            <div key={exp.id} className="relative pl-12 sm:pl-16" data-testid={`experience-card-${exp.id}`}>
              {/* Dot */}
              <div className="absolute left-2.5 sm:left-4 top-5 flex items-center justify-center">
                <span className={`h-4 w-4 rounded-full border-2 border-background ${exp.currentlyWorking ? "bg-primary" : "bg-muted-foreground"}`} />
                {exp.currentlyWorking && (
                  <span className="absolute h-4 w-4 rounded-full bg-primary animate-ping opacity-50" />
                )}
              </div>

              <Card className="p-5 hover:border-foreground/20 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <h3 className="font-display text-base font-semibold">{exp.designation}</h3>
                      {exp.currentlyWorking && (
                        <Badge className="text-[10px] font-mono bg-primary/10 text-primary border-primary/20 hover:bg-primary/10" variant="outline">
                          Current
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                        <Briefcase className="h-3.5 w-3.5" strokeWidth={1.5} />
                        {exp.companyName}
                      </span>
                      {exp.location && (
                        <span className="inline-flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5" strokeWidth={1.5} />
                          {exp.location}
                        </span>
                      )}
                      <span className="font-mono">
                        {formatDate(exp.startDate)} — {exp.currentlyWorking ? "Present" : formatDate(exp.endDate)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{exp.description}</p>
                  </div>
                  <div className="flex sm:flex-col items-center gap-1 shrink-0">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={() => openEdit(exp)}
                      data-testid={`experience-edit-${exp.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => {
                        setToDelete(exp);
                        setConfirmOpen(true);
                      }}
                      data-testid={`experience-delete-${exp.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg" data-testid="experience-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit experience" : "Add experience"}</DialogTitle>
            <DialogDescription>Capture a role in your professional journey.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <FieldRow>
              <Field label="Company">
                <Input
                  data-testid="experience-company"
                  value={form.companyName}
                  onChange={(e) => setForm({ ...form, companyName: e.target.value })}
                  placeholder="Linear"
                />
              </Field>
              <Field label="Designation">
                <Input
                  data-testid="experience-designation"
                  value={form.designation}
                  onChange={(e) => setForm({ ...form, designation: e.target.value })}
                  placeholder="Staff Engineer"
                />
              </Field>
            </FieldRow>
            <FieldRow>
              <Field label="Start Date">
                <Input
                  type="month"
                  data-testid="experience-start"
                  value={form.startDate}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                />
              </Field>
              <Field label="End Date">
                <Input
                  type="month"
                  data-testid="experience-end"
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                  disabled={form.currentlyWorking}
                />
              </Field>
            </FieldRow>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <Label className="text-sm font-medium">Currently working here</Label>
                <p className="text-xs text-muted-foreground">End date will be hidden on your portfolio.</p>
              </div>
              <Switch
                checked={form.currentlyWorking}
                onCheckedChange={(v) => setForm({ ...form, currentlyWorking: v, endDate: v ? "" : form.endDate })}
                data-testid="experience-current-toggle"
              />
            </div>
            <Field label="Location">
              <Input
                data-testid="experience-location"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Remote · San Francisco"
              />
            </Field>
            <Field label="Description">
              <Textarea
                data-testid="experience-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={4}
                placeholder="What you owned, shipped and learned…"
              />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="experience-save">
              {editing ? "Save changes" : "Add experience"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete experience?"
        description="This will remove the role from your portfolio timeline."
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </div>
  );
}

const FieldRow = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
);

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
